import { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Award, Shield, ShieldCheck, Target } from 'lucide-react';
import { DiagnosticFormData } from '../../types/diagnostic';
import { calculateSecurityScore } from '../../utils/securityCalculator';
import { dpsTheme, getScoreLevelConfig } from '../../styles/dpsTheme';

interface SecurityScoreProps {
  formData: DiagnosticFormData;
  showLimited?: boolean;
}

export default function SecurityScore({ formData, showLimited = false }: SecurityScoreProps) {
  const { score, details } = calculateSecurityScore(formData);

  useEffect(() => {
    if (score >= 80) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }
  }, [score]);

  const getBadge = (score: number) => {
    if (score >= 90) return { 
      Icon: Trophy, 
      label: 'Expert en sécurité',
      color: '#fbbf24' // gold
    };
    if (score >= 80) return { 
      Icon: Award, 
      label: 'Maître de la protection',
      color: '#10b981' // emerald
    };
    if (score >= 70) return { 
      Icon: ShieldCheck, 
      label: 'Gardien averti',
      color: '#3b82f6' // blue
    };
    if (score >= 60) return { 
      Icon: Shield, 
      label: 'Protecteur en progression',
      color: '#8b5cf6' // purple
    };
    return { 
      Icon: Target, 
      label: 'Apprenti sécurité',
      color: '#ef4444' // red
    };
  };

  const badge = getBadge(score);
  const scoreLevelConfig = getScoreLevelConfig(score);
  const radius = 85;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const remainingProgress = circumference - progress;

  return (
    <div className="backdrop-blur-xl border border-gray-700/50 rounded-lg p-4 sm:p-6 lg:p-8"
      style={{ background: dpsTheme.colors.background.card }}>
      <div className="text-center mb-6 lg:mb-8">
        <div className="flex justify-center mb-4">
          <badge.Icon
            className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16"
            style={{
              color: badge.color,
              filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
            }}
          />
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-white">{badge.label}</h3>
      </div>

      <div className="flex justify-center mb-6 lg:mb-8">
        <div className="relative w-40 h-40 sm:w-48 sm:h-48 lg:w-64 lg:h-64">
          <svg className="w-full h-full" style={{ transform: 'rotate(-90deg)' }} viewBox="0 0 200 200">
            <circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={dpsTheme.colors.border.secondary}
              strokeWidth="12"
            />
            <motion.circle
              cx="100"
              cy="100"
              r={radius}
              fill="none"
              stroke={scoreLevelConfig.primary}
              strokeWidth="12"
              strokeLinecap="round"
              style={{
                strokeDasharray: `${progress} ${remainingProgress}`,
                transformOrigin: '100px 100px'
              }}
              initial={{ strokeDasharray: `0 ${circumference}` }}
              animate={{ strokeDasharray: `${progress} ${remainingProgress}` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          </svg>

          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="text-center"
            >
              <span className="text-3xl sm:text-4xl lg:text-5xl font-bold"
                style={{ color: scoreLevelConfig.primary }}>
                {score}
              </span>
              <span className="text-lg sm:text-xl lg:text-2xl text-gray-400 ml-1">/100</span>
            </motion.div>
          </div>
        </div>
      </div>

      {!showLimited && (
        <div className="flex flex-col gap-4">
          {details.map((detail, index) => {
            const detailScoreConfig = getScoreLevelConfig(detail.score);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.2 }}
                className="backdrop-blur-sm border border-gray-800 p-3 sm:p-4 rounded-md"
                style={{ background: dpsTheme.colors.background.dark }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-300">{detail.label}</span>
                  <span className="text-xs sm:text-sm font-bold"
                    style={{ color: detailScoreConfig.text }}>
                    {detail.score}/100
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: detailScoreConfig.gradient }}
                    initial={{ width: 0 }}
                    animate={{ width: `${detail.percentage}%` }}
                    transition={{ duration: 1, delay: index * 0.2 }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {showLimited && (
        <div className="border-2 rounded-md p-4 text-center"
          style={{
            background: dpsTheme.colors.accent.blue.bg,
            borderColor: dpsTheme.colors.accent.blue.border
          }}>
          <p className="text-sm sm:text-base font-medium"
            style={{ color: dpsTheme.colors.accent.blue.primary }}>
            Analyse détaillée disponible dans le rapport premium
          </p>
        </div>
      )}
    </div>
  );
}